/**
 * Shared presentation for addons, so the admin catalog card and the paywall describe the
 * same addon the same way.
 *
 * NOTE: every icon named here must also appear in `icon-whitelist.txt`. The FontAwesome
 * scanner only walks `.vue` files and `routes/*.ts` (see
 * `resources/js/vite-plugins/fontawesome-scanner.ts`), so a map living in a plain `.ts`
 * module is invisible to it and the icons are silently dropped from the build --
 * rendering as blank squares rather than failing loudly. Same constraint as
 * `components/admin/eggSpecificTagIcons.ts`.
 */
export const ADDON_ICONS: Record<string, [string, string]> = {
    physgun: ['fas', 'bolt'],
    branding: ['fas', 'palette'],
    'js-injector': ['fas', 'code'],
    'css-injector': ['fas', 'brush'],
    'whmcs-sso': ['fas', 'right-to-bracket'],
    'custom-smtp': ['fas', 'envelope'],
    'egg-switcher': ['fas', 'shuffle'],
    'server-splitter': ['fas', 'code-branch'],
    'backup-explorer': ['fas', 'box-archive'],
    'gmod-backdoor-scanner': ['fas', 'shield-halved'],
    'workshop-downloader': ['fas', 'cloud-arrow-down'],
    'minecraft-version-manager': ['fas', 'layer-group'],
    'fivem-version-manager': ['fas', 'car'],
    'minecraft-plugin-manager': ['fas', 'puzzle-piece'],
    'hytale-plugin-manager': ['fas', 'puzzle-piece'],
    'rust-plugin-manager': ['fas', 'puzzle-piece'],
    'mod-manager': ['fas', 'screwdriver-wrench'],
    'minecraft-modpack-manager': ['fas', 'boxes-stacked'],
    'domain-manager': ['fas', 'globe'],
    metrics: ['fas', 'chart-line'],
};

export const iconForAddon = (name: string | undefined): [string, string] => (name && ADDON_ICONS[name]) || ['fas', 'cube'];

/** Prices are stored in cents. */
export const formatAddonPrice = (cents: number): string =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(cents / 100);

/**
 * Translation key for a plan tier. Deliberately panel-side rather than billing's
 * `display_name`, which reads "WISP - Enterprise Edition" and would leak the brand into
 * whitelabel panels.
 *
 * Returns null for an addon that belongs to no plan -- one sold purely as an add-on.
 * Callers must branch on that rather than render a tier name, or the UI claims the
 * addon is "included with" a plan that does not exist.
 */
export const tierKey = (plan: string | null | undefined): string | null => (plan ? `admin.addons.tiers.${plan}` : null);
